import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Select,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateLiveChannel from "./CreateLiveChannel";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { getTranslatedField, translationsArrayToMap, useLocales, validateNameTranslations } from "utils/translations";
import LocaleNameTabs from "components/localeNameFields/LocaleNameTabs";
import TranslationsCell from "components/localeNameFields/TranslationsCell";

const LIVE_TV_PAGE_ID = PAGE_IDS.LIVE_TV;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyNameTranslation = () => ({ name: "" });

const ListLiveChannel = () => {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editableChannel, setEditableChannel] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "md" });
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === LIVE_TV_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/live-channel?page_id=${LIVE_TV_PAGE_ID}`);
      setChannels(Array.isArray(response) ? response : []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch live channels."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchChannels();
  };

  const handleEdit = (channel) => {
    setEditErrors({});
    setEditableChannel({
      ...channel,
      translations: translationsArrayToMap(channel.translations, emptyNameTranslation, locales),
      slug: channel.slug || "",
      streamType: channel.streamType || "DIRECT",
      streamUrl: channel.streamUrl || "",
      icon: channel.icon || "",
      color: channel.color || "",
      order: channel.order ?? "",
    });
  };

  const setEditTranslationField = (code, value) => {
    setEditableChannel((prev) => ({
      ...prev,
      translations: { ...prev.translations, [code]: { ...emptyNameTranslation(), ...prev.translations[code], name: value } },
    }));
  };

  const handleSave = async () => {
    const errors = validateNameTranslations(editableChannel.translations, locales, t);
    if (!editableChannel.streamUrl.trim()) {
      errors.streamUrl = t("Stream URL is required");
    }
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, name: (editableChannel.translations[code]?.name || "").trim() }))
        .filter((tr) => tr.name);

      const data = {
        page_id: LIVE_TV_PAGE_ID,
        liveChannelId: editableChannel.id,
        translations: translationsPayload,
        slug: editableChannel.slug.trim(),
        streamType: editableChannel.streamType,
        streamUrl: editableChannel.streamUrl.trim(),
        icon: editableChannel.icon.trim(),
        color: editableChannel.color.trim(),
        ...(editableChannel.order !== "" && { order: Number(editableChannel.order) }),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/live-channel`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update live channel");
      }
      await fetchChannels();
      setEditableChannel(null);
      toast({
        title: t("Live channel updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update live channel."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (liveChannelId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/live-channel`,
        "DELETE",
        { page_id: LIVE_TV_PAGE_ID, liveChannelId }
      );
      if (!response) {
        throw new Error("Failed to delete live channel");
      }
      setChannels((prev) => prev.filter((c) => c.id !== liveChannelId));
      toast({
        title: t("Live channel deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("Unable to delete live channel."),
        description: error.message,
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Quick one-click on/off toggle right in the table — this is the actual
  // "enable/disable whenever we want" control; the edit modal below is only
  // needed for changing the channel's details, not for turning it on/off.
  const handleToggleActive = async (channel) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/live-channel`,
        "PUT",
        { page_id: LIVE_TV_PAGE_ID, liveChannelId: channel.id, isActive: !channel.isActive }
      );
      if (!response) {
        throw new Error("Failed to update live channel");
      }
      setChannels((prev) => prev.map((c) => (c.id === channel.id ? { ...c, isActive: !c.isActive } : c)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle channel status."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredChannels = channels.filter((c) => {
    const q = searchQuery.toLowerCase();
    return locales.some(({ code }) => getTranslatedField(c.translations, code).toLowerCase().includes(q));
  });

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Live TV")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Channel")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search channel")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "200px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Name")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Slug")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Stream Type")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredChannels.map((channel) => (
                <Tr key={channel.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td>
                    <TranslationsCell
                      translations={channel.translations}
                      locales={locales}
                      prefix={channel.color && <Box w="10px" h="10px" borderRadius="full" bg={channel.color} flexShrink={0} />}
                    />
                  </Td>
                  <Td>{channel.slug || "—"}</Td>
                  <Td>
                    <Badge colorScheme={channel.streamType === "IFRAME" ? "purple" : "blue"} variant="subtle" px={2} py={1} borderRadius="md" fontSize="0.8em">
                      {channel.streamType === "IFRAME" ? t("Iframe") : t("Direct")}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={channel.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {channel.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={channel.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(channel)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(channel)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDelete(channel.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableChannel && (
        <Modal isOpen={true} onClose={() => setEditableChannel(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Channel")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <LocaleNameTabs
                locales={locales}
                translations={editableChannel.translations}
                errors={editErrors}
                onChange={setEditTranslationField}
                borderColor={GOLD_BORDER}
              />

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Slug")}</Text>
                <Input
                  value={editableChannel.slug}
                  onChange={(e) => setEditableChannel({ ...editableChannel, slug: e.target.value })}
                  {...fieldProps}
                />
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Stream Type")}</Text>
                  <Select
                    value={editableChannel.streamType}
                    onChange={(e) => setEditableChannel({ ...editableChannel, streamType: e.target.value })}
                    {...fieldProps}
                  >
                    <option value="DIRECT">{t("Direct stream (native player)")}</option>
                    <option value="IFRAME">{t("Iframe embed")}</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="sm" color={editErrors.streamUrl ? "red.500" : "gray.600"} mb="2">{t("Stream URL")}</Text>
                  <Input
                    value={editableChannel.streamUrl}
                    onChange={(e) => setEditableChannel({ ...editableChannel, streamUrl: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt="16px">
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Icon")}</Text>
                  <Input
                    value={editableChannel.icon}
                    onChange={(e) => setEditableChannel({ ...editableChannel, icon: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Color")}</Text>
                  <Flex align="center" gap={2}>
                    <Input
                      type="color"
                      value={editableChannel.color || "#C7B45E"}
                      onChange={(e) => setEditableChannel({ ...editableChannel, color: e.target.value })}
                      w="56px"
                      p="4px"
                      {...fieldProps}
                    />
                    <Input
                      value={editableChannel.color}
                      onChange={(e) => setEditableChannel({ ...editableChannel, color: e.target.value })}
                      {...fieldProps}
                    />
                  </Flex>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Order")}</Text>
                  <Input
                    type="number"
                    value={editableChannel.order}
                    onChange={(e) => setEditableChannel({ ...editableChannel, order: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableChannel(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Add Channel")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateLiveChannel onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListLiveChannel;
